<template>
    <nuxt-layout name="main-big" :favorite="true" :dark="!is_search">
        <ModuleTutorial ref="tutorial" />

        <div :class="css.container">
            <!-- * 头部 -->
            <div :class="[css.header, is_search && css.header2]">
                <h1 :class="css.name" class="font-variable color-1 font-bold">{{ $t("MutationViewer") }}</h1>
                <h2 :class="css.descript" class="font-variable color-1 font-bold">{{ $t("MutationViewerVice") }}</h2>
                <!-- * 搜索框 -->
                <item-search
                    ref="item_search"
                    :class="css.search"
                    :placeholder="$t('PleaseEnterAGeneDiseaseOrMutation')"
                    @search="methods.enter"></item-search>
            </div>

            <!-- * 推荐搜索 -->
            <div :class="css.recommend" v-if="!is_search">
                <div :class="css.title">
                    <div :class="css.name" class="font-bold color-1 font-variable">{{ $t("TopSearches") }}</div>
                    <div class="flex justify-between items-center">
                        <i18n-t keypath="HowToUse" scope="global">
                            <template v-slot:view>
                                <span :class="css_normalize.assets" @click="tutorial?.open()">{{ $t("EasyGuide") }}</span>
                            </template>
                        </i18n-t>
                        <div class="w-[1px] h-[12px] mx-[16px] bg-emphas"></div>
                        <div class="transition duration-200 cursor-pointer underline underline-offset-2 hover:text-main" @click="methods.image">
                            {{ $t("TextTutorial") }}
                        </div>
                    </div>
                </div>
                <div :class="css.list">
                    <div :class="css.item" v-for="(value, key) in recommends" :key="key" @click="methods.enter([value])">
                        {{ value.name }}
                    </div>
                </div>
            </div>

            <!-- 检索结果 -->
            <template v-else>
                <div :class="css.result">
                    <div class="container">
                        {{ $t("SearchCriteria") }}：
                        <div v-html="title"></div>
                    </div>
                </div>
                <div class="container">
                    <!-- * 检索条件 -->
                    <div :class="css.control">
                        <div :class="css.search">
                            <default-search
                                v-model="keyword"
                                :width="304"
                                size="middle"
                                :icon="false"
                                :placeholder="$t('SearchByPositionEg')"
                                @search="methods.search()" />
                            <div :class="css.close" class="flex-center" @click="methods.reset(true)" v-show="keyword_search">
                                <ui-icon name="close" />
                            </div>
                        </div>
                        <ui-button :class="css.button" size="middle" :width="80" @click="methods.search()">{{ $t("Search") }}</ui-button>
                        <div :class="css.selects">
                            <ui-select :class="css.select" v-model="gene" v-bind="binds.select" :candidates="genes" @change="methods.acquire(1)" />
                            <ui-select :class="css.select" v-model="mrna" v-bind="binds.select" :candidates="mrnas" @change="methods.acquire(1)" />
                            <ui-select
                                :class="css.select"
                                :candidates="clinicals"
                                v-model="clinical"
                                v-bind="binds.select"
                                @change="methods.acquire(1)" />
                            <ui-button :class="css_button.cancel" size="middle" :width="76" @click="methods.reset()">
                                {{ $t("Reset") }}
                            </ui-button>

                            <div :class="[css.rows, visible && 'ui-form-selector']" class="ui-select">
                                <input :class="css.input" class="ui-form-control" v-bind="binds.selector" @click.stop="methods.open" />
                                <ui-icon name="arrow" :class="css.arrow" class="ui-select-arrow" @click.stop="methods.open" />
                                <transition v-on="animations.tooltip()">
                                    <div :class="css.candidates" class="ui-form-candidates" ref="candidates" v-if="visible">
                                        <!-- * 全选按钮 -->
                                        <ui-checkbox v-model="checkboxs" value="all" @change="methods.show_all">
                                            {{ $t("SelectAll") }}
                                        </ui-checkbox>
                                        <!-- * 多选列表 -->
                                        <ui-checkbox-group v-model="checkbox" @change="methods.show">
                                            <ui-checkbox v-for="value in options.rows" :value="value.value">{{ value.label }}</ui-checkbox>
                                        </ui-checkbox-group>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>

                    <!-- * 表格 -->
                    <diagram
                        ref="table"
                        :class="css.table"
                        v-bind="binds.diagram"
                        :external-tr-attrs="methods.external_attrs"
                        @advanced.sort="methods.sort">
                        <template #clinvar="{ data }">
                            <template v-if="!data.clinvar">--</template>
                            <div v-else style="text-align: center">
                                <span>{{ data.clinvar }}</span>
                                <project-route :to="`https://www.ncbi.nlm.nih.gov/clinvar/variation/${data.clinvar}/`">
                                    <ui-icon :class="css.export" name="export" />
                                </project-route>
                            </div>
                        </template>

                        <template #gene="{ data }">
                            <project-route :class="css_normalize.assets" :to="{ name: 'Gene', params: { id: data.gene_id } }">
                                {{ data.gene }}
                            </project-route>
                        </template>

                        <template #mutation_transcript="{ data }">
                            <template v-if="!data.mutation_transcript">--</template>
                            <template v-else-if="!data.skip">
                                {{ data.mutation_transcript }}
                            </template>
                            <project-route
                                v-else
                                :class="css_normalize.assets"
                                :to="{
                                    name: 'BioVariantResult',
                                    query: { id: data.transcript, mode: '', keyword: data.mutation_transcript },
                                    params: { id: data.gene_id },
                                }">
                                <span> {{ data.mutation_transcript }}</span>
                            </project-route>
                        </template>

                        <template #mutation_protein="{ data }">
                            <template v-if="!data.mutation_protein">--</template>
                            <template v-else-if="!data.skip">{{ data.mutation_protein }}</template>
                            <project-route
                                v-else
                                :class="css_normalize.assets"
                                :to="{
                                    name: 'BioVariantResult',
                                    query: { id: data.transcript, mode: 'codon', keyword: data.mutation_protein },
                                    params: { id: data.gene_id },
                                }">
                                {{ data.mutation_protein }}
                            </project-route>
                        </template>

                        <template #transcript="{ data }">
                            <template v-if="!data.transcript">--</template>
                            <template v-else-if="!data.skip">{{ data.transcript }}</template>
                            <template v-else>
                                <project-route
                                    :class="[css.mrna, css_normalize.assets]"
                                    :to="{ name: 'BioVariantResult', query: { id: data.transcript, mode: 'codon' }, params: { id: data.gene_id } }">
                                    <span>{{ data.transcript }}</span>
                                    <ui-tooltip>
                                        <span :class="css.mrna_label" v-if="data.default || data.default_clinical">
                                            {{ (data.default && "M") || (data.default_clinical && "MP") }}
                                        </span>
                                        <template #content>
                                            {{ (data.default && "MANE Select") || (data.default_clinical && "MANE Plus Clinical ") }}
                                        </template>
                                    </ui-tooltip>
                                </project-route>
                            </template>
                        </template>

                        <template #variant_region="{ data }">
                            <span :class="[css.region, options.variant[data.variant_region]]">{{ data.variant_region }}</span>
                        </template>

                        <template #clinical="{ data }">
                            <div style="text-align: center">
                                {{ data.clinical }}
                                <project-route v-if="data.clinvar" :to="`https://www.ncbi.nlm.nih.gov/clinvar/variation/${data.clinvar}/`">
                                    <ui-icon :class="css.export" name="export" />
                                </project-route>
                            </div>
                        </template>

                        <template #related_diseases="{ data }">
                            <span>{{ data.related_diseases.length }}</span>
                            <ui-tooltip :class-extra-name="css.diseases" direction="bottom" align="start">
                                <span :class="css.add" v-if="data.related_diseases.length">+</span>
                                <template #content>
                                    <span class="color-1 font-bold">{{ $t("RelatedDiseases") }}：</span>
                                    <div :class="css.disease_items">
                                        <template v-for="(value, key) in data.related_diseases">
                                            <project-route :class="css.disease" :to="{ name: 'Disease', params: { id: value.id } }">
                                                {{ value.name }}
                                            </project-route>
                                        </template>
                                    </div>
                                </template>
                            </ui-tooltip>
                        </template>

                        <template #references="{ data }">
                            {{ data.references }}
                            <project-route v-if="data.references > 0" :to="data.references_link">
                                <ui-icon :class="css.export" name="export" />
                            </project-route>
                        </template>
                    </diagram>

                    <!-- * 分页 -->
                    <ui-pagination :class="css.pagination" :count="count" :limit="20" v-model="page" @change="methods.acquire()">
                        <template #items> {{ $t("Layout.PaginationDetails", { count: count, limit: 20 }) }}</template>
                    </ui-pagination>
                </div>
            </template>
        </div>
    </nuxt-layout>
</template>

<script lang="ts" setup>
//* 组件库
import { animations } from "various-ui";
import { UiIcon, UiSelect, UiButton, UiCheckboxGroup, UiCheckbox, UiTooltip, UiPagination } from "various-ui";
//* 公共组件
import { ItemSearch, DefaultSearch } from "@/components/controls";
import { Diagram } from "@/components/visualizations";
//* 私有组件
import ModuleTutorial from "./components/tutorial/index.vue";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css from "./style/index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";
import css_button from "@/assets/style/src/button.module.less";

//* 组合函数
const { refs, nodes, binds, watchs, methods, options, computeds } = useComposable();
const { is_search } = computeds;
const { tutorial, candidates, item_search, table } = nodes;
const {
    keyword_search,
    recommends,
    keyword,
    gene,
    genes,
    mrna,
    mrnas,
    clinical,
    clinicals,
    checkbox,
    checkboxs,
    visible,
    page,
    count,
    title,
    items,
} = refs;

//* 初始化
await methods.init();

onMounted(() => {
    //* 搜索项更新
    nodes.item_search.value?.update(items.value);
});

//* 组件卸载前销毁侦听器和点击事件
onBeforeUnmount(() => {
    watchs.stop && watchs.stop();
    watchs.items && watchs.items();
    document.removeEventListener("click", methods.hidden);
});
</script>
